import { ErrorMessage } from '@hookform/error-message';
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, IconButton, MenuItem, Typography } from '@mui/material';
import classNames from 'classnames';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { WorkflowConfigFormValues } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/component';
import { WorkflowStepField } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog/components/WorkflowStepField';
import {
  WorkflowRoles,
  getWorkflowRolesTranslationKeyFromString,
} from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';
import { theme } from '~/theme';

import { useSxStyles } from './useStyles';

type PropsType = { control: Control<WorkflowConfigFormValues> };

export const WorkflowApprovalSteps = (props: PropsType) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { control } = props;

  // Max steps is something that might be configurable later
  const maxSteps = 7;

  const { errors } = useFormState({
    control,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'approvalSteps',
  });
  const watchInitiator = useWatch({
    control: control,
    name: 'initiators',
  });
  const showFirstApprover = watchInitiator.roleNames.includes(
    WorkflowRoles.FIRST_APPROVER,
  );
  const watchApprovalSteps = useWatch({
    control: control,
    name: 'approvalSteps',
  });

  const handleAddStep = () => {
    append({
      roleNames: [],
      seq: fields.length + 1,
    });
  };

  const isLastStep = (index: number): boolean => fields.length === index + 1;
  const isFirstStep = (index: number): boolean => index === 0;

  const canAddNewStep = useMemo(() => {
    if (watchApprovalSteps.length === 0) {
      return true;
    }

    if (watchApprovalSteps.length >= maxSteps) {
      return false;
    }

    // Can't add new if un-configured steps are present
    if (
      watchApprovalSteps.filter((step) => step.roleNames.length === 0).length >
      0
    ) {
      return false;
    }

    // Can't add new if last step has Responsible HR selected
    const lastStep = watchApprovalSteps[watchApprovalSteps.length - 1];
    return (
      [WorkflowRoles.RESPONSIBLE_HR].filter((roleName) =>
        lastStep.roleNames?.includes(roleName),
      ).length === 0
    );
  }, [watchApprovalSteps]);

  const translateRoleName = (roleName: string) =>
    t(getWorkflowRolesTranslationKeyFromString(roleName));

  return (
    <Box sx={sxStyles.approvalStepsRoot}>
      <Box>
        {t('workflowApprovalProcessBlueprint')}
        <Typography variant={'caption'} component={'p'}>
          {t('workflowApprovalProcessBlueprintDescription')}
        </Typography>
        <ErrorMessage
          errors={errors}
          name={'approvalSteps'}
          render={({ message }) => (
            <Typography
              variant={'caption'}
              component={'p'}
              sx={{ color: theme.palette.error.main }}
            >
              {message}
            </Typography>
          )}
        />
      </Box>
      {fields.map((field, index) => {
        return (
          <Box key={`approvalStep-${index}`} className={'stepBox'}>
            <Box className={'stepBoxTitle'}>
              <Box>
                {`${t('workflowApprovalStep')} ${field.seq}`}
                {isFirstStep(index) && showFirstApprover && (
                  <Typography
                    component={'span'}
                    className={classNames('relativeRole firstApprover')}
                  >
                    {t('firstApprover')}
                  </Typography>
                )}
                {isLastStep(index) && (
                  <Typography
                    component={'span'}
                    className={classNames('relativeRole finalApprover')}
                  >
                    {t('finalApprover')}
                  </Typography>
                )}
              </Box>
              {!isFirstStep(index) && (
                <IconButton
                  onClick={() => remove(index)}
                  disabled={!isLastStep(index)}
                  size={'small'}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
            <Controller
              control={control}
              name={`approvalSteps.${index}`}
              render={({ field: { value, onChange, name }, formState }) => {
                return (
                  <>
                    {!!value && (
                      <WorkflowStepField
                        stepConfig={value}
                        onChange={(newValue) =>
                          onChange({
                            ...newValue,
                            seq: value.seq,
                          })
                        }
                        roleOptions={[
                          WorkflowRoles.MANAGER,
                          // WorkflowRoles.ALT_MANAGER, // To be enabled later, when BE supports
                          ...(isLastStep(index)
                            ? [WorkflowRoles.RESPONSIBLE_HR]
                            : []),
                        ]}
                        max={1}
                        renderAddMenuItem={({
                          option,
                          onSelect,
                          isSelected,
                          selection,
                        }) => {
                          const isEmptySelection = selection.length === 0;

                          const canSelectOption = (opt: string) => {
                            switch (opt) {
                              case WorkflowRoles.ALT_MANAGER:
                                return selection.includes(
                                  WorkflowRoles.MANAGER,
                                );
                              case WorkflowRoles.RESPONSIBLE_HR:
                                return isEmptySelection && isLastStep(index);
                              default:
                                return true;
                            }
                          };

                          return (
                            <MenuItem
                              key={option}
                              onClick={() => onSelect(option)}
                              disabled={isSelected || !canSelectOption(option)}
                            >
                              {translateRoleName(option)}
                            </MenuItem>
                          );
                        }}
                        sx={{ margin: theme.spacing(0) }}
                        fieldName={name}
                        formErrors={formState.errors}
                      />
                    )}
                  </>
                );
              }}
            />
          </Box>
        );
      })}
      <Button
        onClick={handleAddStep}
        fullWidth={true}
        startIcon={<Add />}
        disabled={!canAddNewStep}
      >
        {t('workflowAddNewStep')}
      </Button>
    </Box>
  );
};
